import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, MapPin, Package, Ruler, Lock, Star, ShieldCheck, Layers, Tag, Calendar, Hash, Disc, Award, FileText } from 'lucide-react';
import { cn, fmt2, fmtEUR, pricePerUnit } from '@/lib/utils';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('de-DE', { year: '2-digit', month: '2-digit', day: '2-digit' });
  } catch (e) {
    return '—';
  }
};

/**
 * Compact, image-free list row for the materials browse page.
 * Shows only text information for fast scannability.
 */
export const MaterialListRow = ({ material, onAddToWatchlist, isInWatchlist, optionalColumns = [] }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const showThickness = optionalColumns.includes('thickness');
  const showCategory = optionalColumns.includes('category');
  const showCreated = optionalColumns.includes('created_at');
  const showPartNumber = optionalColumns.includes('manufacturer_part_number');
  const showGrade = optionalColumns.includes('material_grade');
  const showCoreDiameter = optionalColumns.includes('core_diameter');
  const showMaterialIndex = optionalColumns.includes('material_index');
  const showDescription = optionalColumns.includes('common_description');
  const showRolls = optionalColumns.includes('number_of_rolls');
  const showSpecial = optionalColumns.includes('is_special');

  const tier = material.access_tier || (user?.approved ? 3 : user ? 2 : 1);
  const locked = tier < 3;

  const locationParts = [material.site_city, material.site_country].filter(Boolean);
  const locationStr = locationParts.join(', ');

  const handleWatchlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAddToWatchlist?.(material.material_id);
  };

  return (
    <Link
      to={`/material/${material.material_id}`}
      className="group block bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 hover:border-orange-400 dark:hover:border-orange-500 transition-colors"
      data-testid={`material-list-row-${material.material_id}`}
    >
      <div className="flex items-center gap-4 px-4 py-2.5">
        {/* Title + manufacturer */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3
              className="font-manrope font-semibold text-sm text-slate-900 dark:text-slate-100 group-hover:text-orange-500 transition-colors truncate"
              data-testid="material-title"
            >
              {material.common_title}
            </h3>
            {material.is_special && (
              <Badge className="bg-amber-500 text-white text-[10px] gap-1 px-1.5 py-0">
                <Star className="h-2.5 w-2.5" />
                {t('badge.special', 'Spezial')}
              </Badge>
            )}
            {tier >= 3 && material.is_internal && (
              <Badge variant="secondary" className="bg-blue-500 text-white text-[10px] px-1.5 py-0">
                {t('badge.groupOnly', 'Gruppe')}
              </Badge>
            )}
            {tier >= 3 && material.has_missing_docs && (
              <Badge variant="outline" className="text-orange-600 border-orange-300 text-[10px] px-1.5 py-0">
                {t('badge.missingDocs', 'Docs fehlen')}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5" data-testid="material-manufacturer">
            {material.manufacturer_verified && <ShieldCheck className="h-3 w-3 text-emerald-500 shrink-0" />}
            <span className="truncate">{material.manufacturer_name || '—'}</span>
          </p>
        </div>

        {/* Optional: Description */}
        {showDescription && (
          <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground flex-1 min-w-0" data-testid="material-description">
            <FileText className="h-3 w-3 shrink-0" />
            <span className="truncate">{material.common_description || '—'}</span>
          </div>
        )}

        {/* Optional: Manufacturer part number */}
        {showPartNumber && (
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-32 shrink-0" data-testid="material-part-number">
            <Hash className="h-3 w-3 shrink-0" />
            <span className="truncate">{material.manufacturer_part_number || '—'}</span>
          </div>
        )}

        {/* Optional: Material index */}
        {showMaterialIndex && (
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-24 shrink-0" data-testid="material-index">
            <span className="truncate">{material.material_index || '—'}</span>
          </div>
        )}

        {/* Format / Width */}
        <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-24 shrink-0" data-testid="material-width">
          <Ruler className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {material.material_width_mm ? `${material.material_width_mm} mm` : '—'}
          </span>
        </div>

        {/* Optional: Thickness */}
        {showThickness && (
          <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-24 shrink-0" data-testid="material-thickness">
            <Layers className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {material.material_thickness_optional ? `${material.material_thickness_optional} µm` : '—'}
            </span>
          </div>
        )}

        {/* Optional: Core diameter */}
        {showCoreDiameter && (
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-20 shrink-0" data-testid="material-core-diameter">
            <Disc className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {material.core_diameter_optional ? `${material.core_diameter_optional} mm` : '—'}
            </span>
          </div>
        )}

        {/* Optional: Material grade */}
        {showGrade && (
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-24 shrink-0" data-testid="material-grade">
            <Award className="h-3 w-3 shrink-0" />
            <span className="truncate">{material.material_grade_optional || '—'}</span>
          </div>
        )}

        {/* Optional: Category */}
        {showCategory && (
          <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground w-32 shrink-0" data-testid="material-category">
            <Tag className="h-3 w-3 shrink-0" />
            <span className="truncate">{material.category_name || '—'}</span>
          </div>
        )}

        {/* Optional: Special flag */}
        {showSpecial && (
          <div className="hidden md:flex items-center gap-1 text-xs w-20 shrink-0" data-testid="material-is-special">
            {material.is_special ? (
              <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <Star className="h-3 w-3 fill-current" />
                {t('badge.special', 'Spezial')}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        )}

        {/* SKU count */}
        <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-24 shrink-0" data-testid="material-sku-count">
          <Package className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {material.sku_count > 0
              ? `${material.sku_count} ${material.sku_count === 1 ? t('material.position', 'Position') : t('material.positions', 'Positionen')}`
              : '—'}
          </span>
        </div>

        {/* Optional: Number of rolls */}
        {showRolls && (
          <div className="hidden md:flex items-center gap-1 text-xs text-muted-foreground w-20 shrink-0" data-testid="material-rolls">
            <Disc className="h-3 w-3 shrink-0" />
            <span className="truncate">
              {material.number_of_rolls ? material.number_of_rolls : '—'}
            </span>
          </div>
        )}

        {/* Location */}
        <div className="hidden lg:flex items-center gap-1 text-xs text-muted-foreground w-40 shrink-0" data-testid="material-location">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{locationStr || '—'}</span>
        </div>

        {/* Availability (quantity) */}
        <div className="hidden md:block w-28 shrink-0 text-right text-xs" data-testid="material-quantity">
          {locked ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Lock className="h-3 w-3" />
              {t('material.locked', 'Gesperrt')}
            </span>
          ) : material.total_quantity != null ? (
            <span className="text-slate-700 dark:text-slate-300">
              {fmt2(material.total_quantity)} {material.uom}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Optional: Created at */}
        {showCreated && (
          <div className="hidden md:flex items-center justify-end gap-1 text-xs text-muted-foreground w-28 shrink-0" data-testid="material-created-at">
            <Calendar className="h-3 w-3 shrink-0" />
            <span className="truncate">{formatDate(material.created_at)}</span>
          </div>
        )}

        {/* Price */}
        <div className="w-28 sm:w-32 shrink-0 text-right" data-testid="material-pricing">
          {locked ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground justify-end">
              <Lock className="h-3 w-3" />
              {t('material.price', 'Preis')}
            </span>
          ) : material.total_book_value_kEUR > 0 ? (
            <>
              <p className="text-sm font-bold text-orange-500 leading-tight">
                &euro;{fmtEUR(material.total_book_value_kEUR)}
              </p>
              {material.total_quantity > 0 && (
                <p className="text-[10px] text-orange-400 font-medium leading-tight">
                  {fmt2(pricePerUnit(material.total_book_value_kEUR * 1000, material.total_quantity))} EUR/{material.uom}
                </p>
              )}
            </>
          ) : (
            <span className="text-xs text-muted-foreground">
              {t('material.priceOnRequest', 'Preis auf Anfrage')}
            </span>
          )}
        </div>

        {/* Watchlist */}
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8 shrink-0', isInWatchlist && 'text-red-500')}
          onClick={handleWatchlist}
          data-testid={`watchlist-btn-${material.material_id}`}
          aria-label={t('common.watchlist', 'Merkliste')}
        >
          <Heart className={cn('h-4 w-4', isInWatchlist && 'fill-current')} />
        </Button>
      </div>
    </Link>
  );
};
